import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { 
    ChevronLeft, RefreshCw, Users, ShieldAlert, Award, Grid, ListTodo, Building, Calendar, Download
} from "lucide-react";
import { 
    fetchConsolidationCampagneRequest
} from "../../../redux/actions/performanceActions";
import PerformanceConsolidationKpis from "./PerformanceConsolidationKpis";
import PerformanceCollaborateursTable from "./PerformanceCollaborateursTable";
import PerformanceManagersTable from "./PerformanceManagersTable";
import PerformanceEntitiesTable from "./PerformanceEntitiesTable";
import PerformanceMissingDataTable from "./PerformanceMissingDataTable";
import PerformanceProgressDetailModal from "./PerformanceProgressDetailModal";
import "./PerformanceConsolidation.css";

export default function PerformanceConsolidationDashboard() {
    const { campagneId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const consolidationState = useSelector((s) => s.performance?.consolidation || {});
    const data = consolidationState.byCampagneId?.[String(campagneId)];
    const loading = consolidationState.loadingByCampagneId?.[String(campagneId)];
    const error = consolidationState.errorByCampagneId?.[String(campagneId)];

    const campaigns = useSelector((s) => s.campagnes?.list || []);
    const selectedCampaign = campaigns.find((c) => String(c.id) === String(campagneId));

    const [activeTab, setActiveTab] = useState("collaborateurs"); // collaborateurs, managers, entities, alerts
    const [selectedProgressId, setSelectedProgressId] = useState(null);

    useEffect(() => {
        if (campagneId) {
            dispatch(fetchConsolidationCampagneRequest(campagneId));
        }
    }, [dispatch, campagneId]);

    const formatDate = (date) => {
        if (!date) return "—";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("fr-FR");
    };

    const calculateParticipation = () => {
        const total = data?.totalCollaborateurs || 0;
        const nonDemarres = data?.notStartedCount || 0;
        if (total === 0) return "0%";
        const rate = ((total - nonDemarres) / total) * 100;
        if (rate % 1 === 0) {
            return `${rate}%`;
        }
        return `${rate.toFixed(2).replace(".", ",")}%`;
    };

    const handleOpenDetail = (progressId) => {
        setSelectedProgressId(progressId);
    };

    const handleBack = () => {
        navigate("/performance/rh/consolidation");
    };

    const tabConfigs = [
        { id: "collaborateurs", label: "Collaborateurs", icon: Users, badge: data?.collaborateurs?.length || 0 },
        { id: "managers", label: "Synthèse Managers", icon: Award, badge: data?.managers?.length || 0 },
        { id: "entities", label: "Synthèse Entités", icon: Grid, badge: data?.entities?.length || 0 },
        // { id: "alerts", label: "Alertes & Données Manquantes", icon: ShieldAlert, badge: data?.missingData?.length || 0, isAlert: true }
    ];

    return (
        <div className="perf-cons-container">
            {/* Back Button */}
            <button className="perf-cons-back-btn" onClick={handleBack}>
                <ChevronLeft size={16} /> Retour aux campagnes
            </button>

            {/* Header Toolbar - Bloc Campagne */}
            <div className="perf-campaign-banner">
                <div className="perf-campaign-left">
                    <div className="perf-campaign-icon-box">
                        <Building size={20} />
                    </div>
                    
                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Campagne</span>
                        <span className="perf-campaign-value highlighted-name">
                            {selectedCampaign?.nom || data?.campagneNom || `Campagne #${campagneId}`}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Début</span>
                        <span className="perf-campaign-value">
                            <Calendar size={14} className="perf-campaign-date-icon" />
                            {formatDate(selectedCampaign?.dateDebut)}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Fin</span>
                        <span className="perf-campaign-value">
                            <Calendar size={14} className="perf-campaign-date-icon-fin" />
                            {formatDate(selectedCampaign?.dateFin)}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Collaborateurs</span>
                        <span className="perf-campaign-value highlighted-collabs">
                            {selectedCampaign?.filterMode === "IN" && selectedCampaign?.userIds?.length 
                                ? "Collaborateurs ciblés" 
                                : "Tous les collaborateurs"}
                        </span>
                    </div>

                    <div className="perf-campaign-divider" />

                    <div className="perf-campaign-col">
                        <span className="perf-campaign-label">Participation</span>
                        <span className="perf-campaign-value highlighted-participation">
                            {calculateParticipation()}
                        </span>
                    </div>
                </div>

                <div className="perf-campaign-actions">
                    <button className="perf-banner-btn perf-banner-btn--extract">
                        <Download size={14} /> Extraire le Bilan
                    </button>
                    <button className="perf-banner-btn perf-banner-btn--prolong">
                        <Calendar size={14} /> Prolonger
                    </button>
                    <button className="perf-banner-btn perf-banner-btn--relaunch">
                        <RefreshCw size={14} /> Relancer
                    </button>
                </div>
            </div>

            {/* Error States */}
            {error && (
                <div className="perf-state perf-state--error">
                    {error}
                </div>
            )}

            {/* Loading skeleton or overlay */}
            {loading && !data && (
                <div className="perf-state perf-state--inline" style={{ textAlign: "center", padding: "60px" }}>
                    <RefreshCw size={24} className="animate-spin" style={{ margin: "0 auto 12px", color: "#204385" }} />
                    Chargement de la consolidation de la campagne...
                </div>
            )}

            {!loading && !error && !data && (
                <div className="perf-state perf-state--warning">
                    Aucune donnée de consolidation disponible pour cette campagne de performance.
                </div>
            )}

            {/* KPI Cards & Tabs Grid */}
            {data && (
                <>
                    {/* KPIs Panel */}
                    <PerformanceConsolidationKpis kpis={data} />

                    {/* Tab Navigation */}
                    <div className="perf-cons-tabs">
                        {tabConfigs.map(tab => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    className={`perf-cons-tab-btn ${activeTab === tab.id ? "active" : ""} ${tab.isAlert ? "alert-tab" : ""}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <IconComponent size={15} />
                                    <span>{tab.label}</span>
                                    <span className="perf-cons-tab-badge">{tab.badge}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Tab content panel */}
                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)" }}>
                        {activeTab === "collaborateurs" && (
                            <PerformanceCollaborateursTable 
                                collaborateurs={data.collaborateurs} 
                                onViewDetail={handleOpenDetail} 
                            />
                        )}

                        {activeTab === "managers" && (
                            <PerformanceManagersTable 
                                managers={data.managers} 
                            />
                        )}

                        {activeTab === "entities" && (
                            <PerformanceEntitiesTable 
                                entities={data.entities} 
                            />
                        )}

                        {activeTab === "alerts" && (
                            <PerformanceMissingDataTable 
                                missingData={data.missingData} 
                                collaborateurs={data.collaborateurs}
                                onViewDetail={handleOpenDetail} 
                            />
                        )}
                    </div>
                </>
            )}

            {/* Individual Progress Detail Modal */}
            {selectedProgressId && (
                <PerformanceProgressDetailModal 
                    progressId={selectedProgressId} 
                    onClose={() => setSelectedProgressId(null)} 
                />
            )}
        </div>
    );
}
